import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNitroCAStore } from "../../../../store/CompetitiveAnalysisStore";
import { useNitroStore } from "../../../../store/Store";
import IRouteValidationResult from "./types/IRouteValidation";
import RouteValidationService from "./lib/service";
import "./index.css";
import RequestPanel from "./components/requestPanel";
import FindMissingOrigin from "./components/findMissingOriginPanel";
import AddMissingOrigin from "./components/addMissingOriginPanel";
import OriginAlreadyAdded from "./components/originAlreadyAddedPanel";
import NitroRequestService from "../../../../utils/nitroRequest.service";
import NotificationPanel from "./components/notificationPanel";
import StatusPanel from "./components/StatusPanel";

const RouteValidation = ({ reload }: { reload: () => void }) => {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [
    routeValidationOpen,
    setRouteValidationOpen,
    resetRouteValidationState,
  ] = useNitroCAStore((state) => [
    state.routeValidationOpen,
    state.setRouteValidationOpen,
    state.resetRouteValidationState,
  ]);

  const reset = () => {
    loadTrackerRef.current = null;
    initialValidationRef.current = null;
    setInitialValidation(null);
    setValidation(null);
    setOccupant({ id: "", name: "" });
    setOrigin({ id: "", name: "" });
  };

  const [selectedLocation, product, mode, currentFilters, showInsightsPanel] =
    useNitroStore((state) => [
      state.selectedLocation,
      state.product,
      state.mode,
      state.currentFilters,
      state.showInsightsPanel,
    ]);
  const [triggerReload] = useNitroCAStore((state) => [state.triggerReload]);

  const [origin, setOrigin] = useState<{
    id: string;
    name: string;
    existsInRoutes?: boolean;
  }>({ id: "", name: "", existsInRoutes: false });
  const [occupant, setOccupant] = useState({ id: "", name: "" });

  const [validation, setValidation] = useState<IRouteValidationResult | null>(
    null,
  );
  const [initialValidation, setInitialValidation] =
    useState<IRouteValidationResult | null>(null);
  const initialValidationRef = useRef<IRouteValidationResult | null>(null);
  const loadTrackerRef = useRef<string | null>(null);

  const [showNotification, setShowNotification] = useState(false);
  type ViewState =
    | "LOOKUP_FORM"
    | "ORIGIN_ALRREDY_ADDED"
    | "TASK_LIST"
    | "SUCCESS_RATE_MISSING"
    | "SEARCH_FOR_RAIL_RATE"
    | "ADD_MISSING_ORIGIN_COMPLETE"
    | "MILES_NEEDED_COMPLETE"
    | "TRUCK_RATE_MISSING_COMPLETE"
    | "ADD_FAILURE";
  const [currentView, setCurrentView] = useState<ViewState>("LOOKUP_FORM");

  const [pendingCompletion, setPendingCompletion] =
    useState<IRouteValidationResult | null>(null);

  const handleOriginFinalized = (finalizedOrigin: any) => {
    setOrigin(finalizedOrigin);
  };

  const handleOccupantSelect = (selectedOccupant: any) => {
    setOccupant(selectedOccupant);
  };

  // This occupant update triggers a useEffect hook to navigate to the next panel
  useEffect(() => {
    if (origin.id === "" || origin.existsInRoutes === undefined) {
      return;
    }

    if (!origin.existsInRoutes && occupant.id) {
      setCurrentView("TASK_LIST");
    }
  }, [origin, occupant]);

  useEffect(() => {
    if (resetRouteValidationState > 0) {
      reset();
      setCurrentView("LOOKUP_FORM");
    }
  }, [resetRouteValidationState]);

  const sendApiErrorEmail = useCallback(
    async (apiName: string, errMsg: string) => {
      try {
        await NitroRequestService.postApiErrorRequest({
          marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
          product,
          mode,
          origin: origin.name,
          originZipCode: validation?.originZipCode ?? "",
          destination: selectedLocation?.name || "",
          destinationZipCode: validation?.destinationZipCode ?? "",
          comments: "",
          api: apiName,
          message: errMsg,
        });
      } catch (error) {
        console.error("postApiErrorRequest - Failed to send email:", error);
      }
    },
    [auth.user, product, mode, origin.name, validation, selectedLocation],
  );

  const sendRateMissingEmail = useCallback(async () => {
    try {
      await NitroRequestService.postRailRateMissingRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
        mode,
        origin: origin.name,
        originZipCode: validation?.originZipCode ?? "",
        destination: selectedLocation?.name || "",
        destinationZipCode: validation?.destinationZipCode ?? "",
        comments: "",
      });
    } catch (error) {
      console.error(
        "Requests/missingRateRequest - Failed to send email:",
        error,
      );
      setCurrentView("ADD_FAILURE");
      sendApiErrorEmail(
        "requests/missingRateRequest",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
    }
  }, [
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    product,
    mode,
    origin.name,
    validation?.originZipCode,
    validation?.destinationZipCode,
    selectedLocation?.name,
    sendApiErrorEmail,
  ]);

  const sendEmail = useCallback(async () => {
    try {
      await NitroRequestService.postMileageRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
        mode,
        origin: origin.name,
        originZipCode: validation?.originZipCode ?? "",
        destination: selectedLocation?.name || "",
        destinationZipCode: validation?.destinationZipCode ?? "",
        comments: "",
      });
    } catch (error) {
      console.error("MileageRequest - Failed to send email:", error);
    }
  }, [
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    product,
    mode,
    origin.name,
    validation?.originZipCode,
    validation?.destinationZipCode,
    selectedLocation?.name,
  ]);

  const sendRateReviewEmail = useCallback(async () => {
    try {
      await NitroRequestService.postReviewRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        origin: origin.name,
        originZipCode: validation?.originZipCode ?? "",
        destination: selectedLocation?.name || "",
        destinationZipCode: validation?.destinationZipCode ?? "",
        customerName: "",
        mode,
        railCarriers: "",
        product,
        month: currentFilters.month,
        comments: "",
      });
    } catch (error) {
      console.error("ReviewRequest - Failed to send email:", error);
    }
  }, [
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    origin.name,
    validation?.originZipCode,
    validation?.destinationZipCode,
    selectedLocation?.name,
    mode,
    product,
    currentFilters.month,
  ]);

  const sendSQSMessage = useCallback(async () => {
    try {
      if (!selectedLocation) {
        throw new Error("No selected location available for cache refresh.");
      }
      await NitroRequestService.postCacheRefresh("destination", {
        destinationId: selectedLocation.id,
      });
    } catch (error) {
      setCurrentView("ADD_FAILURE");
      sendApiErrorEmail(
        "cache-refresh",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
      console.error("postCacheRefresh - Failed to send SQS message:", error);
    }
  }, [selectedLocation, sendApiErrorEmail]);

  const pollRouteValidation = useCallback(() => {
    const POLL_INTERVAL_MS = 15 * 1000; // 15 seconds
    const TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
    const startTime = Date.now();
    const intervalId = setInterval(async () => {
      try {
        const result = await RouteValidationService.validate({
          mode,
          product,
          originLocationId: origin.id,
          originOccupantId: occupant.id || "",
          destinationId: selectedLocation?.id || "",
          shipDate: currentFilters.month,
          isCity: !!selectedLocation?.city,
        });
        if (result.hasRailCache) {
          setCurrentView("ADD_MISSING_ORIGIN_COMPLETE");
          setShowNotification(true);
          clearInterval(intervalId);
          sendRateReviewEmail();
          return;
        }

        if (Date.now() - startTime >= TIMEOUT_MS) {
          console.log(
            "Timeout: Rail cache not available after 3 minutes. Sending rate review email.",
          );
          sendRateReviewEmail();
          clearInterval(intervalId);
        }
      } catch (error) {
        sendRateReviewEmail();
        console.error("Polling API call failed:", error);
        clearInterval(intervalId); // Stop polling on error
      }
    }, POLL_INTERVAL_MS);
  }, [
    mode,
    product,
    origin.id,
    occupant.id,
    selectedLocation?.id,
    selectedLocation?.city,
    currentFilters.month,
    sendRateReviewEmail,
  ]);

  const handleComplete = useCallback(
    (result: any) => {
      if (origin.existsInRoutes) {
        setCurrentView("ORIGIN_ALRREDY_ADDED");
        return;
      }
      if (
        mode === "TRUCK" &&
        result.hasTruckRoute &&
        result.hasValidTruckRateForShipWindow &&
        !result.hasTruckMileage
      ) {
        setCurrentView("MILES_NEEDED_COMPLETE");
        sendEmail();
      } else if (
        mode === "TRUCK" &&
        (!result.hasTruckRoute || !result.hasValidTruckRateForShipWindow)
      ) {
        setCurrentView("TRUCK_RATE_MISSING_COMPLETE");
        sendRateMissingEmail();
      } else if (
        mode === "RAIL" &&
        result.hasRailRouteFromOrigin &&
        result.hasRailRouteFromServingOriginCarrier &&
        result.hasRailRouteToDestination &&
        result.hasRailRouteToServingDestinationCarrier &&
        !result.hasRailCache
      ) {
        setCurrentView("SEARCH_FOR_RAIL_RATE");
        sendSQSMessage();
        pollRouteValidation();
      } else if (
        mode === "RAIL" &&
        (!result.hasRailRouteFromOrigin ||
          !result.hasRailRouteFromServingOriginCarrier ||
          !result.hasRailRouteToDestination ||
          !result.hasRailRouteToServingDestinationCarrier ||
          !result.railRouteCanBeBuilt)
      ) {
        setCurrentView("SUCCESS_RATE_MISSING");
        sendRateMissingEmail();
      } else if (
        mode === "TRUCK" &&
        result.hasOutboundMode &&
        result.hasKochOrCompetitorOccupant &&
        result.originCanStoreProduct &&
        result.hasTruckRoute &&
        result.hasValidTruckRateForShipWindow &&
        result.hasInboundMode &&
        result.inStateTheyCompeteIn &&
        result.hasTruckMileage
      ) {
        setCurrentView("ADD_MISSING_ORIGIN_COMPLETE");
      } else {
        setCurrentView("ADD_FAILURE");
        sendApiErrorEmail(
          "RouteValidation",
          `Uncovered case - No origin added: ${JSON.stringify(
            result,
            null,
            2,
          )}`,
        );
      }
    },
    [
      origin.existsInRoutes,
      mode,
      sendEmail,
      sendSQSMessage,
      pollRouteValidation,
      sendRateMissingEmail,
      sendApiErrorEmail,
    ],
  );

  useEffect(() => {
    if (pendingCompletion) {
      handleCompleteRef.current(pendingCompletion);
      setPendingCompletion(null);
    }
  }, [pendingCompletion]);

  const handleCompleteRef = useRef(handleComplete);
  useEffect(() => {
    handleCompleteRef.current = handleComplete;
  }, [handleComplete]);

  const sendApiErrorEmailRef = useRef(sendApiErrorEmail);

  // Keep the ref updated with the latest version of the function on every render
  useEffect(() => {
    sendApiErrorEmailRef.current = sendApiErrorEmail;
  }, [sendApiErrorEmail]);

  const load = useCallback(async () => {
    if (!origin.id || !occupant.id) return;

    setValidation(null);
    setIsLoading(true);
    try {
      const result = await RouteValidationService.validate({
        mode,
        product,
        originLocationId: origin.id,
        originOccupantId: occupant.id,
        destinationId: selectedLocation?.id || "",
        shipDate: currentFilters.month,
        isCity: !!selectedLocation?.city,
      });

      setValidation(result as any);
      if (!initialValidation) {
        setInitialValidation(JSON.parse(JSON.stringify(result)));
      }
    } catch (err) {
      setCurrentView("ADD_FAILURE");
      sendApiErrorEmailRef.current(
        "/RouteValidation?",
        JSON.stringify(err, Object.getOwnPropertyNames(err), 2),
      );
      console.error(err);
    }
    setIsLoading(false);
  }, [
    origin.id,
    occupant.id,
    mode,
    product,
    selectedLocation?.id,
    selectedLocation?.city,
    currentFilters.month,
    initialValidation,
  ]);

  useEffect(() => {
    if (!validation) {
      return;
    }

    initialValidationRef.current ??= JSON.parse(JSON.stringify(validation));

    const checks = [
      !initialValidation?.inStateTheyCompeteIn,
      !initialValidation?.originCanStoreProduct,
      !initialValidation?.hasOutboundMode,
      mode.toLowerCase() === "rail" &&
        initialValidation?.hasRailRouteFromOrigin &&
        !initialValidation?.hasRailRouteFromServingOriginCarrier,
      !initialValidation?.destinationCanStoreProduct,
      !initialValidation?.hasInboundMode,
      mode.toLowerCase() === "rail" &&
        initialValidation?.hasRailRouteToDestination &&
        !initialValidation?.hasRailRouteToServingDestinationCarrier,
    ];
    const totalNum = checks.filter(Boolean).length;

    if (totalNum === 0) {
      handleCompleteRef.current(validation);
    }
  }, [validation, initialValidation, mode]);

  useEffect(() => {
    if (!selectedLocation || !product || !mode || !origin.id || !occupant.id) {
      setValidation(null);
      return;
    }

    const dependencyKey = `${origin.id}-${occupant.id}-${mode}-${product}-${selectedLocation.id}`;

    if (dependencyKey === loadTrackerRef.current) {
      return;
    }

    loadTrackerRef.current = dependencyKey;
    load();
  }, [selectedLocation, product, mode, origin, occupant, load]);

  useEffect(() => {
    if (!showInsightsPanel) {
      load();
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInsightsPanel]);

  if (!routeValidationOpen || !selectedLocation) {
    return null;
  }

  const handleFlowClose = () => {
    setRouteValidationOpen(false);
    setCurrentView("LOOKUP_FORM");
    reset();
    triggerReload();
  };

  const handleChangeOrigin = () => {
    setCurrentView("LOOKUP_FORM");
    reset();
  };

  const renderCurrentPanel = () => {
    switch (currentView) {
      case "TASK_LIST":
        return (
          <AddMissingOrigin
            selectedOrigin={origin}
            selectedOccupant={occupant}
            validation={initialValidation}
            onChange={handleChangeOrigin}
            onCompleteChange={handleComplete}
          />
        );
      case "SUCCESS_RATE_MISSING":
        return (
          <StatusPanel
            originName={origin.name}
            onClose={handleFlowClose}
            title="Rail Rate Missing"
            message={
              <>
                <p>
                  Looks like you did the hard work to add the origin, but the
                  rail rate is still missing.
                </p>
                <p>
                  No worries-we&apos;ve sent an email to the team to get
                  everything sorted. We&apos;ll let you know as soon as the
                  origin is added!
                </p>
              </>
            }
            buttonText="Close"
          />
        );
      case "SEARCH_FOR_RAIL_RATE":
        return (
          <StatusPanel
            originName={origin.name}
            onClose={handleFlowClose}
            title="Searching For Rail Rates"
            message={
              <p>
                Attempting to add this origin. Please note the rate won&apos;t
                be available for use until it&apos;s verified.
              </p>
            }
            buttonText="Close"
          />
        );
      case "ADD_MISSING_ORIGIN_COMPLETE":
        return (
          <StatusPanel
            originName={origin.name}
            onClose={handleFlowClose}
            title="Tasks Complete - Origin Added"
            message={
              <p>
                The origin is now available, you&apos;re all set to return to
                quoting.
              </p>
            }
            buttonText="Return to Quoting"
          />
        );
      case "MILES_NEEDED_COMPLETE":
        return (
          <StatusPanel
            originName={origin.name}
            onClose={handleFlowClose}
            title="Miles Needed"
            message={
              <>
                <p>
                  Looks like you did the hard work to add the origin, but we
                  still need to grab the miles to get everything in place.
                </p>
                <p>
                  They&apos;re required for the origin to show up, so we&apos;ve
                  sent an email to request them. We&apos;ll let you know as soon
                  as the origin is added!
                </p>
              </>
            }
            buttonText="Return to Quoting"
          />
        );
      case "TRUCK_RATE_MISSING_COMPLETE":
        return (
          <StatusPanel
            originName={origin.name}
            onClose={handleFlowClose}
            title="Truck Rate Missing"
            message={
              <>
                <p>
                  Looks like you did the hard work to add the origin, but it
                  looks like logistics isn&apos;t calling guidance for this OD
                  pair yet.
                </p>
                <p>
                  No worries-we&apos;ve sent an email to the team to get
                  everything sorted. We&apos;ll let you know as soon as the
                  origin is added!
                </p>
              </>
            }
            buttonText="Return to Quoting"
          />
        );

      case "ORIGIN_ALRREDY_ADDED":
        return (
          <OriginAlreadyAdded
            originName={origin.name}
            onChange={handleChangeOrigin}
            onShowMe={handleFlowClose}
          />
        );
      case "ADD_FAILURE":
        return (
          <StatusPanel
            originName={origin.name}
            onClose={handleFlowClose}
            title="Oops! Something Went Wrong"
            message={
              <p>
                We&apos;ve notified our team and will let you know once
                it&apos;s fixed—thank you for your patience
              </p>
            }
            buttonText="Close"
          />
        );
      default:
        return (
          <FindMissingOrigin
            origin={origin}
            setOrigin={handleOriginFinalized}
            occupant={occupant}
            setOccupant={handleOccupantSelect}
          />
        );
    }
  };

  const getTitle = () => {
    switch (currentView) {
      case "LOOKUP_FORM":
        return "Find Missing Origin";
      case "ORIGIN_ALRREDY_ADDED":
        return "Add Missing Origin";
      case "TASK_LIST":
      case "SUCCESS_RATE_MISSING":
      case "SEARCH_FOR_RAIL_RATE":
        return "Add Missing Origin";
      case "ADD_MISSING_ORIGIN_COMPLETE":
      case "MILES_NEEDED_COMPLETE":
      case "TRUCK_RATE_MISSING_COMPLETE":
        return "Troubleshoot Missing Origin";
      default:
        return "Find Missing Origin";
    }
  };
  return (
    <>
      {showNotification && (
        <NotificationPanel
          title="Tasks Complete - Origin Added"
          message="The origin is now available, you're all set to return to quoting."
          onClose={() => setShowNotification(false)}
        />
      )}
      <RequestPanel
        isSaving={false}
        isLoading={isLoading}
        goBack={handleFlowClose}
        title={getTitle()}
      >
        {renderCurrentPanel()}
      </RequestPanel>
    </>
  );
};

export default RouteValidation;
